import { Helmet } from 'react-helmet-async';
import { useOrg } from '@/contexts/OrgContext';
import { useLocalSeoData } from '@/hooks/useLocalSeo';
import { useIsDemoRoute } from '@/hooks/useTenantPath';
import { buildLocalBusinessJsonLd, defaultSeoDescription, serializeJsonLd } from '@/lib/localSeo';
import { isTenantHost, tenantBrowserOrigin } from '@/lib/tenantHost';

/**
 * schema.org LocalBusiness JSON-LD for the tenant homepage. api/tenant-page.ts
 * renders the same object (same @id) into the HTML for crawlers that don't run
 * JavaScript; this keeps it present after client-side navigation.
 *
 * No aggregateRating / review markup on purpose — see buildLocalBusinessJsonLd.
 */
export function LocalBusinessJsonLd() {
  const { orgId, org } = useOrg();
  const isDemo = useIsDemoRoute();
  const onTenantHost = isTenantHost();
  const { data } = useLocalSeoData(onTenantHost && !isDemo ? orgId : null);

  if (!data || !onTenantHost || isDemo) return null;

  const businessName = data.company?.company_name?.trim() || org.name;
  const jsonLd = buildLocalBusinessJsonLd({
    origin: tenantBrowserOrigin(data.customDomain),
    businessName,
    company: data.company,
    services: data.services,
    hours: data.hours,
    description: org.description || org.tagline || defaultSeoDescription({ kind: 'home' }, { businessName, company: data.company, services: data.services }),
    image: org.coverImageUrl || data.company?.logo_url || org.logoUrl,
  });

  return (
    <Helmet>
      <script type="application/ld+json">{serializeJsonLd(jsonLd)}</script>
    </Helmet>
  );
}
